import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Breadcrumbs, Button, LottieLoader, Notification } from '@mondra/ui-components'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { usePrompt } from '../../../hooks/usePrompt'
import { showCorrelationIdError } from '../../../redux/correlationIdSlice'
import {
  handlePublishStatus,
  setDefaultComponentTaxonomy,
} from '../../../redux/componentTaxonomySlice'
import { ActivityTypes, ROUTES } from '../../../const'
import InputItem from '../../../components/InputItem'
import ActivityRightPanel from '../../../components/ActivityRightPanel'
import UnsavedChangesWarning from '../../../components/UnsavedChangesWarning'
import MaterialForm from './components/MaterialForm'
import MaterialRow from './components/MaterialRow'
import { fetchComponentTaxonomy, loadConfiguration } from './componentTaxonomyActions'
import {
  createComponentTaxonomy,
  publishComponentTaxonomy,
  unpublishComponentTaxonomy,
  updateComponentTaxonomy,
} from './api'
import { transformFormValues } from '../../../shared/formFunctions'
import { IComponentMaterialRow, IComponentMaterialRowWrite } from './types'
import { formSchema } from './utils'
import './index.scss'

const ComponentTaxonomyItem: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { componentTaxonomy, configuration, loading } = useAppSelector(
    state => state.componentTaxonomySlice
  )

  const [showError, setShowError] = useState('')
  const [showNotification, setShowNotification] = useState(false)

  const itemId = searchParams.get('itemId') || ''
  const isNew = itemId === 'new'

  useEffect(() => {
    if (!itemId) {
      navigate('/not-found')
      return
    }
    dispatch(loadConfiguration(isNew ? undefined : Number(itemId)))
    if (!isNew) {
      dispatch(fetchComponentTaxonomy(Number(itemId), () => navigate(ROUTES.componentTaxonomy)))
    }
    return () => {
      dispatch(setDefaultComponentTaxonomy())
    }
  }, [isNew])

  const showApiError = useCallback(
    (error: any, fallback: string) => {
      if (error.response?.data?.correlationId) {
        dispatch(showCorrelationIdError(error.response.data.correlationId))
      }
      const message =
        typeof error.response?.data === 'string'
          ? error.response.data
          : typeof error.response?.data?.errors === 'string'
          ? error.response.data.errors
          : error.response?.data?.errors
          ? Object.values(error.response.data.errors).join('\n')
          : error.message || fallback
      setShowError(message)
      setTimeout(() => setShowError(''), 5000)
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: { ...componentTaxonomy },
    validationSchema: formSchema,
    onSubmit: async values => {
      // Every row is coerced through the shared transform first: values from an Input arrive as
      // strings and an unset Select as -1, while the wire contract is numbers and nulls. Carrying
      // only the two taxonomy ids here was the bug that silently dropped the four combination
      // fields on save.
      const madeOfMaterials: IComponentMaterialRowWrite[] = values.madeOfMaterials.map(m => {
        const row = transformFormValues(m)
        return {
          defaultProcessingActivityId: row.defaultProcessingActivityId,
          defaultRecycledContent: row.defaultRecycledContent,
          eiName: row.eiName,
          // Already a tri-state boolean in form state, so the unset case stays null and never
          // collapses to false or true.
          isExactMatch: m.isExactMatch ?? null,
          // Exactly one of the two taxonomy references is sent, per the API's exclusive-ref rule.
          packagingMaterialCategoryId: m.packagingMaterialFunctionalUnitId
            ? null
            : row.packagingMaterialCategoryId,
          packagingMaterialFunctionalUnitId: m.packagingMaterialFunctionalUnitId
            ? row.packagingMaterialFunctionalUnitId
            : null,
        }
      })

      try {
        if (isNew) {
          await createComponentTaxonomy({
            alias: values.alias,
            madeOfMaterials,
            name: values.name,
          })
          navigate(ROUTES.componentTaxonomy)
        } else {
          await updateComponentTaxonomy({
            alias: values.alias,
            id: Number(itemId),
            madeOfMaterials,
            name: values.name,
          })
          dispatch(fetchComponentTaxonomy(Number(itemId), () => navigate(ROUTES.componentTaxonomy)))
          setShowNotification(true)
          setTimeout(() => setShowNotification(false), 5000)
        }
      } catch (error: any) {
        showApiError(error, 'Failed to save')
      }
    },
  })

  useEffect(() => {
    formik.setValues({ ...componentTaxonomy })
  }, [componentTaxonomy])

  // "Includes processing impacts" is owned by the material emission factor record, keyed by
  // material taxonomy functional unit. A category-level row never resolves to one and so never
  // warns, by design: the flag cannot be evaluated at category level.
  const materialEmissionImpactsById = useMemo(
    () => new Map(configuration.materialEmissionImpacts.map(mei => [mei.functionalUnitId, mei])),
    [configuration.materialEmissionImpacts]
  )

  const addMaterial = useCallback(
    (row: IComponentMaterialRow) => {
      formik.setValues({
        ...formik.values,
        madeOfMaterials: [...formik.values.madeOfMaterials, row],
      })
    },
    [formik]
  )

  const deleteMaterial = useCallback(
    (index: number) => {
      formik.setValues({
        ...formik.values,
        madeOfMaterials: formik.values.madeOfMaterials.filter((_, i) => i !== index),
      })
    },
    [formik]
  )

  const handleSave = useCallback(() => {
    formik.handleSubmit()
  }, [formik])

  const handleCancel = useCallback(() => {
    navigate(ROUTES.componentTaxonomy)
  }, [navigate])

  const handlePublish = useCallback(async () => {
    try {
      await publishComponentTaxonomy(Number(itemId))
      dispatch(handlePublishStatus(1))
    } catch (error: any) {
      showApiError(error, 'Failed to publish')
    }
  }, [itemId, dispatch, showApiError])

  const handleUnpublish = useCallback(async () => {
    try {
      await unpublishComponentTaxonomy(Number(itemId))
      dispatch(handlePublishStatus(0))
    } catch (error: any) {
      showApiError(error, 'Failed to unpublish')
    }
  }, [itemId, dispatch, showApiError])

  const isDirty = !isNew && JSON.stringify(componentTaxonomy) !== JSON.stringify(formik.values)

  // The API rejects a publish with no active materials (400). Reflect that in the button rather
  // than spending a round trip on a click that cannot succeed - the page already explains why.
  const hasMaterials = componentTaxonomy.madeOfMaterials?.length > 0

  usePrompt('There are unsaved changes, do you want to leave?', isDirty)

  if (loading && !isNew) {
    return (
      <div className="overlay">
        <LottieLoader
          lottieType="butterflyLottie"
          style={{ height: '10.25rem', width: '10.25rem' }}
        />
      </div>
    )
  }

  return (
    <>
      <div className="piece content">
        {showError && (
          <div className="piece-notification z-dialog">
            <Notification description={showError} isToast={false} label="Error:" type="error" />
          </div>
        )}
        {showNotification && (
          <div className="piece-notification z-dialog">
            <Notification
              description="Item was updated!"
              isToast={false}
              label="Success:"
              type="success"
            />
          </div>
        )}

        <div className="mb-3 mt-3">
          <Breadcrumbs
            routes={[
              { path: ROUTES.componentTaxonomy, title: 'Component Taxonomy' },
              {
                path: `${ROUTES.componentTaxonomyItem}?itemId=${itemId}`,
                title: isNew ? 'New Component Taxonomy' : componentTaxonomy.name,
              },
            ]}
          />
        </div>

        {!isNew && (
          <div className="piece-header">
            <h2 className="text-2xl">{componentTaxonomy.name}</h2>
            <div
              className={`piece-header-status ${
                componentTaxonomy.state === 0 ? 'bg-red-500' : 'bg-emerald-500'
              }`}
            >
              {componentTaxonomy.state === 0 ? 'Not Published' : 'Published'}
            </div>
          </div>
        )}

        <form className="component-taxonomy-form" onSubmit={formik.handleSubmit}>
          <h5 className="mt-5">Component Details</h5>
          <div className="component-taxonomy-inputs">
            <InputItem
              defaultValue={formik.values.name}
              description="Enter the component name"
              formik={formik}
              formikName="name"
              name="Component name"
              onChange={formik.handleChange}
              showAsterisk
              type="input"
            />
            <InputItem
              defaultValue={formik.values.alias || ''}
              description="Enter an optional alias for this component"
              formik={formik}
              formikName="alias"
              name="Alias"
              onChange={formik.handleChange}
              type="input"
            />
          </div>

          <h5 className="mt-8">Made of</h5>
          <p className="mb-3 text-sm text-gray-500">
            Valid materials this component can be made from. Links to material taxonomy entries.
          </p>

          <div className="component-taxonomy-collapsible">
            {formik.values.madeOfMaterials.length === 0 && (
              <p className="mb-2 text-sm text-gray-400">
                No valid materials added yet. Add at least one material before publishing.
              </p>
            )}
            {formik.values.madeOfMaterials.map((row: IComponentMaterialRow, index: number) => (
              <MaterialRow
                key={row.id}
                formik={formik}
                includesProcessingImpacts={Boolean(
                  row.packagingMaterialFunctionalUnitId != null &&
                    materialEmissionImpactsById.get(row.packagingMaterialFunctionalUnitId)
                      ?.includesProcessingImpacts
                )}
                index={index}
                processingActivities={configuration.packagingProcessingActivities}
                row={row}
                onDelete={deleteMaterial}
              />
            ))}
            <MaterialForm
              addMaterial={addMaterial}
              configuration={configuration}
              existingMaterials={formik.values.madeOfMaterials}
            />
          </div>

          <div className="component-taxonomy-actions">
            <UnsavedChangesWarning isDirty={!isNew && isDirty} />
            <Button onClick={handleCancel} variant="tertiary">
              Cancel
            </Button>
            <Button disabled={formik.isSubmitting} onClick={handleSave} variant="primary">
              {isNew ? 'Create' : 'Save'}
            </Button>
            {!isNew &&
              (componentTaxonomy.state === 0 ? (
                <Button
                  disabled={isDirty || !hasMaterials}
                  onClick={handlePublish}
                  variant="secondary"
                >
                  Publish
                </Button>
              ) : (
                <Button onClick={handleUnpublish} variant="secondary">
                  Unpublish
                </Button>
              ))}
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={itemId}
        activityType={ActivityTypes.ComponentTaxonomy}
        auditCreatedBy={componentTaxonomy.researcherName || ''}
        auditLastUpdatedBy={componentTaxonomy.lastUpdatedName}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
        hideCompanyselect
      />
    </>
  )
}

export default ComponentTaxonomyItem
